import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ShareWhatsApp from "@/components/ShareWhatsApp";

export default function Bills() {
    const { user } = useAuth();
    const [bills, setBills] = useState([]);
    const isAdmin = user?.role === "admin";
    const [form, setForm] = useState({ tower: "", flat_no: "", amount: "", period: "", due_date: "" });

    const load = async () => {
        try { const r = await api.get(isAdmin ? "/bills" : "/bills/me"); setBills(r.data); } catch {}
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

    const create = async (e) => {
        e.preventDefault();
        try {
            const r = await api.post("/bills", { ...form, amount: parseFloat(form.amount) });
            setBills((arr) => [r.data, ...arr]);
            setForm({ tower: "", flat_no: "", amount: "", period: "", due_date: "" });
            toast.success("Bill created");
        } catch { toast.error("Failed"); }
    };

    const pay = async (b) => {
        try {
            const r = await api.post(`/bills/${b.bill_id}/pay`);
            setBills((arr) => arr.map((x) => x.bill_id === b.bill_id ? r.data : x));
            toast.success("Marked as paid");
        } catch { toast.error("Failed"); }
    };

    const total = bills.filter((b) => b.status === "unpaid").reduce((s, b) => s + b.amount, 0);

    return (
        <div className="animate-fade-up" data-testid="bills-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 sm:gap-4">
                <div>
                    <p className="nest-stat-label">Maintenance</p>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1 sm:mt-2">{isAdmin ? "Society dues" : "Your bills"}</h1>
                    <p className="text-nest-ink2 text-sm sm:text-base mt-1 sm:mt-2">{isAdmin ? "Issue bills to flats, monitor collection." : `Unpaid total · ₹${total.toFixed(2)}`}</p>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 mt-6 sm:mt-10">
                {isAdmin && (
                    <form onSubmit={create} className="nest-card p-6 self-start" data-testid="bill-form">
                        <p className="nest-stat-label">Create bill</p>
                        <h3 className="font-display text-xl mt-1 tracking-tight">Issue a new dues entry</h3>
                        <div className="mt-5 grid gap-4">
                            <div className="grid grid-cols-2 gap-3">
                                <input required value={form.tower} onChange={(e) => setForm({ ...form, tower: e.target.value })} placeholder="Tower" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-tower-input"/>
                                <input required value={form.flat_no} onChange={(e) => setForm({ ...form, flat_no: e.target.value })} placeholder="Flat no." className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-flat-input"/>
                            </div>
                            <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount (₹)" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-amount-input"/>
                            <input required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="Period (e.g., Feb 2026)" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-period-input"/>
                            <input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-duedate-input"/>
                            <button type="submit" className="nest-btn-primary" data-testid="bill-submit-btn">Create bill</button>
                        </div>
                    </form>
                )}
                <div className={`${isAdmin ? "lg:col-span-2" : "lg:col-span-3"} nest-card overflow-hidden`}>
                    <div className="hidden md:grid grid-cols-12 gap-3 p-4 bg-nest-alt nest-stat-label border-b border-nest-line">
                        <div className="col-span-3">Flat</div>
                        <div className="col-span-3">Period</div>
                        <div className="col-span-2">Amount</div>
                        <div className="col-span-2">Due</div>
                        <div className="col-span-2 text-right">Status</div>
                    </div>
                    {bills.length === 0 && <div className="p-10 text-center text-nest-ink2" data-testid="no-bills">No bills yet.</div>}
                    {bills.map((b) => (
                        <div key={b.bill_id} className="border-b border-nest-line hover:bg-nest-alt/25 transition" data-testid={`bill-${b.bill_id}`}>
                            {/* Mobile Card View (< md) */}
                            <div className="md:hidden p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-sm text-nest-ink bg-nest-alt px-2.5 py-1 rounded-lg border border-nest-line">
                                            {b.tower}-{b.flat_no}
                                        </span>
                                        <span className="text-xs text-nest-ink2 font-medium">{b.period}</span>
                                    </div>
                                    {b.status === "paid" ? (
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-nest-sage text-white" data-testid={`bill-paid-${b.bill_id}`}>Paid</span>
                                    ) : (
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-500 text-white">Unpaid</span>
                                    )}
                                </div>
                                <div className="flex items-baseline justify-between pt-1">
                                    <div>
                                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">Due by</span>
                                        <span className="text-xs text-nest-ink2">{b.due_date}</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">Total Amount</span>
                                        <span className="font-display text-xl font-bold text-nest-ink">₹{b.amount.toFixed(2)}</span>
                                    </div>
                                </div>
                                {b.status !== "paid" && (
                                    <div className="flex items-center gap-2 pt-2 border-t border-nest-line/60">
                                        <button onClick={() => pay(b)} className="flex-1 text-xs py-2 rounded-xl bg-nest-ink text-white font-semibold text-center hover:bg-nest-clay transition" data-testid={`bill-pay-${b.bill_id}`}>Mark Paid</button>
                                        <div className="flex-1">
                                            <ShareWhatsApp
                                                testid={`bill-share-${b.bill_id}`}
                                                label="Remind on WhatsApp"
                                                text={`🧾 Maintenance reminder for flat *${b.tower}-${b.flat_no}*\nPeriod: ${b.period}\nAmount: ₹${b.amount.toFixed(2)}\nDue: ${b.due_date}\n— NeighborNest`}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Desktop / Laptop Table Row (>= md) */}
                            <div className="hidden md:grid grid-cols-12 gap-3 p-4 items-center">
                                <div className="col-span-3 font-semibold text-nest-ink">{b.tower}-{b.flat_no}</div>
                                <div className="col-span-3 text-sm text-nest-ink2">{b.period}</div>
                                <div className="col-span-2 font-display text-base font-bold text-nest-ink">₹{b.amount.toFixed(2)}</div>
                                <div className="col-span-2 text-sm text-nest-ink2">{b.due_date}</div>
                                <div className="col-span-2 flex justify-end gap-2 flex-wrap">
                                    {b.status === "paid" ? (
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-nest-sage text-white" data-testid={`bill-paid-${b.bill_id}`}>Paid</span>
                                    ) : (
                                        <>
                                            <button onClick={() => pay(b)} className="text-xs px-3 py-1.5 rounded-lg bg-nest-ink text-white hover:bg-nest-clay transition" data-testid={`bill-pay-${b.bill_id}`}>Mark paid</button>
                                            <ShareWhatsApp
                                                testid={`bill-share-${b.bill_id}`}
                                                label="Remind"
                                                text={`🧾 Maintenance reminder for flat *${b.tower}-${b.flat_no}*\nPeriod: ${b.period}\nAmount: ₹${b.amount.toFixed(2)}\nDue: ${b.due_date}\n— NeighborNest`}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
